import {
  BarChart3,
  Building2,
  Calendar,
  CheckSquare,
  FileText,
  Globe,
  Home,
  Layers,
  Settings,
  ShieldAlert,
  Upload,
  Users,
  Webhook,
} from '@/components/icons';
import type { TFunction } from 'i18next';
import { actionRegistry } from './registry';
import type { CommandAction } from './types';

/**
 * Registers the default set of navigation + admin actions. Called once at
 * app bootstrap (`apps/web/src/main.tsx`) with the i18n `t()` and a
 * navigate function (e.g. `navigate` from `useNavigate()` re-exposed via a
 * helper, or `window.location.assign` as a coarse fallback).
 *
 * Admin actions reference the `admin.translations` resource which already
 * exists on the W4-F5 RBAC matrix; partner-only actions (partner settings)
 * gate on `org / manage`.
 */
export function seedCommandActions(
  t: TFunction,
  navigate: (path: string) => void,
): void {
  const actions: CommandAction[] = [
    // ---- Navigation
    {
      id: 'nav.dashboard',
      label: t('commandPalette.actions.goToDashboard'),
      group: 'navigation',
      icon: Home,
      run: () => navigate('/dashboard'),
    },
    {
      id: 'nav.buildings',
      label: t('commandPalette.actions.goToBuildings'),
      group: 'navigation',
      icon: Building2,
      run: () => navigate('/buildings'),
      permissions: [{ resource: 'building', action: 'read' }],
    },
    {
      id: 'nav.portfolios',
      label: t('commandPalette.actions.goToPortfolios'),
      group: 'navigation',
      icon: Layers,
      run: () => navigate('/portfolios'),
      permissions: [{ resource: 'portfolio', action: 'read' }],
    },
    {
      id: 'nav.analytics',
      label: t('commandPalette.actions.goToAnalytics'),
      group: 'navigation',
      icon: BarChart3,
      run: () => navigate('/analytics'),
    },
    {
      id: 'nav.reports',
      label: t('commandPalette.actions.goToReports'),
      group: 'navigation',
      icon: FileText,
      run: () => navigate('/analytics/reports'),
    },
    {
      id: 'nav.notifications',
      label: t('commandPalette.actions.goToNotifications'),
      group: 'navigation',
      icon: CheckSquare,
      run: () => navigate('/notifications'),
    },
    {
      id: 'nav.settings',
      label: t('commandPalette.actions.goToSettings'),
      group: 'navigation',
      icon: Settings,
      run: () => navigate('/settings'),
    },
    {
      id: 'nav.owner',
      label: t('commandPalette.actions.goToOwnerHome'),
      group: 'navigation',
      icon: Home,
      run: () => navigate('/owner'),
      // W41 owner home is partner-tier gated. `org/manage` is the closest
      // existing matrix entry; partner-admin role satisfies it.
      permissions: [{ resource: 'org', action: 'manage' }],
    },
    {
      id: 'nav.marketplace',
      label: t('commandPalette.actions.goToMarketplace'),
      group: 'navigation',
      icon: Layers,
      run: () => navigate('/marketplace'),
    },

    // ---- Admin
    {
      id: 'admin.imports-new',
      label: t('commandPalette.actions.startDataImport'),
      group: 'admin',
      icon: Upload,
      run: () => navigate('/admin/imports/new'),
      permissions: [{ resource: 'org', action: 'manage' }],
    },
    {
      id: 'admin.users',
      label: t('commandPalette.actions.manageUsers'),
      group: 'admin',
      icon: Users,
      run: () => navigate('/admin/users'),
      permissions: [{ resource: 'org', action: 'manage' }],
    },
    {
      id: 'admin.audit-log',
      label: t('commandPalette.actions.openAuditLog'),
      group: 'admin',
      icon: FileText,
      run: () => navigate('/admin/logs'),
      permissions: [{ resource: 'org', action: 'manage' }],
    },
    {
      id: 'admin.compliance-calendar',
      label: t('commandPalette.actions.openComplianceCalendar'),
      group: 'admin',
      icon: Calendar,
      run: () => navigate('/admin/compliance-calendar'),
      permissions: [{ resource: 'org', action: 'manage' }],
    },
    {
      id: 'admin.translations',
      label: t('commandPalette.actions.openTranslations'),
      group: 'admin',
      icon: Globe,
      run: () => navigate('/admin/translations'),
      permissions: [{ resource: 'admin.translations', action: 'manage' }],
    },
    {
      id: 'admin.partner-settings',
      label: t('commandPalette.actions.openPartnerSettings'),
      group: 'admin',
      icon: Settings,
      run: () => navigate('/admin/partner-settings'),
      permissions: [{ resource: 'org', action: 'manage' }],
    },
    {
      id: 'admin.webhooks',
      label: t('commandPalette.actions.openWebhookAdmin'),
      group: 'admin',
      icon: Webhook,
      run: () => navigate('/admin/integrations/webhooks'),
      permissions: [{ resource: 'org', action: 'manage' }],
    },
    {
      id: 'admin.support',
      // Stable id for a future W10-F5 support page. The action is admin-gated
      // and currently navigates to a stub; replace once W10-F5 ships.
      label: t('commandPalette.actions.openSupportTickets'),
      group: 'admin',
      icon: ShieldAlert,
      run: () => navigate('/admin/support/tickets'),
      permissions: [{ resource: 'org', action: 'manage' }],
    },
  ];

  for (const action of actions) {
    actionRegistry.register(action);
  }
}
